/**
 * CMS 메뉴·라우트 권한 단일 설정 (docs/V1_ADMIN_MENU_RESTRUCTURE.md §3-1)
 * Sidebar(메뉴 표시) · App RoleRoute(접근 허용) · navigation.test.js 가 모두 이 파일만 읽는다.
 * Phase 1: 기존 권한표를 그대로 옮김. Phase 2(A): 그룹 재배치·Seasonal 메뉴 통합 — 역할별 접근 권한은 그대로
 *   (docs/V1_ADMIN_SCREEN_REVIEW.md §4, knowledge-hub 12.01-D008).
 */

const ALL = ['admin', 'designer', 'store_owner', 'store_manager', 'tlj_manager', 'tlj_admin'];

// icon: Sidebar.jsx 의 ICONS 맵 키 (MUI 아이콘 이름)
// module: platform 모듈 (src/config/platformModules.js) — 그룹 라벨·대표 색. 하위 메뉴 아이콘은 회색
export const NAV_GROUPS = [
  {
    id: 'home',
    label: null,
    items: [{ path: '/', label: 'Dashboard', icon: 'Dashboard', roles: ALL }],
  },
  {
    id: 'content', module: 'device',
    label: '콘텐츠',
    items: [
      { path: '/content', label: 'Content', icon: 'TextSnippet', roles: ['admin', 'designer'] },
      { path: '/playlist', label: 'Playlist', icon: 'List', roles: ['admin', 'designer'] },
      { path: '/screen', label: 'Screen', icon: 'DesktopMac', roles: ['admin', 'store_owner'] },
      { href: '/Photovid/', external: true, label: 'PhotoVid', title: 'Photo to Video', icon: 'Slideshow', roles: ['admin', 'designer'] },
    ],
  },
  {
    id: 'menu', module: 'sales',
    label: '메뉴·가격',
    items: [
      { path: '/menu-master', label: 'Menu Master', icon: 'MenuBook', roles: ['admin', 'designer'] },
      // Seasonal + Seasonal Products 를 메뉴 하나로 (결정 ①) — /seasonal-products 는 탭으로 이동, HIDDEN_ROUTES
      { path: '/seasonal', label: 'Seasonal', icon: 'EventNote', roles: ['admin'] },
    ],
  },
  {
    id: 'requests', module: 'service',
    label: '요청',
    items: [
      { path: '/requests', label: 'Requests', icon: 'Assignment', roles: ['admin', 'designer', 'store_owner', 'store_manager'] },
      { path: '/service-requests', label: 'Service Requests', icon: 'Category', roles: ALL, feature: 'serviceRequestV2', fallback: '/requests' },
    ],
  },
  {
    id: 'store', module: 'core',
    label: '매장',
    items: [
      { path: '/store', label: 'Store', icon: 'Store', roles: ['admin', 'designer', 'store_owner', 'tlj_manager', 'tlj_admin'] },
      // 신규 점포 온보딩 보드 (12.01-D011) — 단계별 진행·사유 입력
      { path: '/onboarding', label: 'Onboarding', icon: 'Flag', roles: ['admin', 'tlj_admin'] },
      { path: '/quick-setup', label: 'Quick Setup', icon: 'RocketLaunch', roles: ['admin', 'store_owner', 'store_manager', 'tlj_manager', 'tlj_admin'] },
      { path: '/storeportal', label: 'My Store', icon: 'Storefront', roles: ['store_owner', 'store_manager', 'tlj_manager', 'tlj_admin'] },
      // TLJ 메뉴보드: 지역 매니저 저장 → 이미지 자동 생성 (12.01-D014)
      { path: '/menu-board', label: 'Menu Board', icon: 'ViewQuilt', roles: ['admin', 'designer', 'store_owner', 'store_manager', 'tlj_manager', 'tlj_admin'] },
      { path: '/tlj', label: 'TLJ EAST/WEST', icon: 'Hub', roles: ['tlj_admin'] },
    ],
  },
  {
    id: 'device', module: 'device',
    label: '디바이스·현장',
    items: [
      // Devices 허브: 기기·명령 / 상태(System Health) / WG·OTA(Provisioning) 탭 — 결정 ③④
      { path: '/devices', label: 'Devices', icon: 'DeviceHub', roles: ['admin'] },
      { path: '/tv-remote', label: 'TV Remote', icon: 'Tv', roles: ['admin', 'designer', 'store_owner'] },
      { path: '/field-visits', label: 'Field Visits', icon: 'Build', roles: ['admin', 'store_owner', 'tlj_manager', 'tlj_admin'] },
      // Quick Setup 을 쓰는 역할에게 현장 키트 다운로드 링크
      { href: '/field-kit/', external: true, label: 'Field Kit', title: '현장 APK 키트 다운로드', icon: 'Download', roles: ['admin', 'store_owner', 'store_manager', 'tlj_manager', 'tlj_admin'] },
    ],
  },
  {
    id: 'finance', module: 'finance',
    label: '정산',
    items: [
      { path: '/invoices', label: 'Invoices', icon: 'ReceiptLong', roles: ['admin'] },
      { path: '/payment', label: 'Payment', icon: 'Payments', roles: ['admin'] },
    ],
  },
  {
    id: 'admin', module: 'core',
    label: '관리',
    items: [
      { path: '/user', label: 'User', icon: 'PersonAdd', roles: ['admin', 'designer'] },
      { path: '/activity-log', label: 'Activity Log', icon: 'History', roles: ['admin'] },
      { path: '/admin-settings', label: 'Settings', icon: 'Settings', roles: ['admin'] },
    ],
  },
];

// 메뉴에는 없지만 접근 허용되는 경로
export const HIDDEN_ROUTES = [
  // 예전 경로 → /devices?tab=… 리다이렉트 (북마크 유지)
  { path: '/device-hub', roles: ['admin'] },
  { path: '/health', roles: ['admin'] },
  { path: '/provisioning', roles: ['admin'] },
  { prefix: '/devices/', roles: ['admin'] }, // 기기 상세 /devices/:code (결정 ⑥)
  { path: '/seasonal-products', roles: ['admin'] }, // Seasonal 탭 (메뉴는 /seasonal 하나)
  { path: '/tlj', roles: ['admin'] },
  { path: '/storeportal', roles: ['admin'] },
  { prefix: '/setting/', roles: ['admin', 'store_owner'] },
  { prefix: '/pricing/', roles: ['admin', 'store_owner'] },
];

const featureOn = (item, features) => !item.feature || !!(features && features[item.feature]);

const allItems = () => NAV_GROUPS.flatMap((g) => g.items);

/** 이 역할이 pathname 에 접근할 수 있는가 (RoleRoute) */
export function canAccess(role, pathname, features) {
  if (!role) return false;
  const item = allItems().find((i) => i.path === pathname);
  if (item) {
    if (!featureOn(item, features)) return false;
    if (item.roles.includes(role)) return true;
  }
  return HIDDEN_ROUTES.some((h) =>
    h.roles.includes(role) && (h.path ? h.path === pathname : pathname.startsWith(h.prefix)),
  );
}

/** 접근 불가 시 이동할 곳 — 기능 플래그로 꺼진 메뉴는 대체 경로 (예: /service-requests → /requests) */
export function redirectFor(pathname, features) {
  const item = allItems().find((i) => i.path === pathname);
  if (item && item.fallback && !featureOn(item, features)) return item.fallback;
  return '/';
}

/** 역할별 사이드바 그룹 — 보이는 항목이 없는 그룹은 제외 */
export function visibleGroups(role, features) {
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => role && i.roles.includes(role) && featureOn(i, features)),
  })).filter((g) => g.items.length > 0);
}
